import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { API_BASE } from '../lib/api'
import { Logo } from '../components/Logo'
import '../styles/public-roadmap.css'
import '../styles/roadmap.css'

type Msg = { id: string; body: string; author: string | null; fromAdmin: boolean; createdAt: string }
type Ticket = { request: { title: string; description: string; status: string }; thread: Msg[] }

const fmt = (s: string) => new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export default function TicketConversationPage() {
    const { token } = useParams<{ token: string }>()
    const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
    const [ticket, setTicket] = useState<Ticket | null>(null)
    const [error, setError] = useState('')
    const [reply, setReply] = useState('')
    const [sending, setSending] = useState(false)

    const load = () => {
        fetch(`${API_BASE}/api/roadmap/ticket/${token}`)
            .then(async res => {
                const j = await res.json().catch(() => ({}))
                if (res.ok) { setTicket(j); setState('ready') }
                else { setError(j.error ?? 'This link is invalid or has expired.'); setState('error') }
            })
            .catch(() => { setError('Could not reach the server. Please try again later.'); setState('error') })
    }
    useEffect(() => {
        if (!token) { setState('error'); setError('Invalid link.'); return }
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const send = async () => {
        if (!reply.trim()) return
        setSending(true)
        try {
            const res = await fetch(`${API_BASE}/api/roadmap/ticket/${token}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: reply.trim() }),
            })
            if (res.ok) { setReply(''); load() }
        } finally { setSending(false) }
    }

    return (
        <div className="public-roadmap-root">
            <header className="public-roadmap-header">
                <div className="public-roadmap-header-inner">
                    <div className="public-roadmap-brand">
                        <Logo height={24} />
                        <span className="public-roadmap-subtitle">Product Roadmap</span>
                    </div>
                    <div className="public-roadmap-header-actions">
                        <Link to="/roadmap" className="btn btn-secondary">← View roadmap</Link>
                    </div>
                </div>
            </header>

            <main className="public-roadmap-content">
                {state === 'loading' && (
                    <div className="confirm-card"><div className="confirm-icon"><div className="spinner" /></div><h2>Loading…</h2></div>
                )}

                {state === 'error' && (
                    <div className="confirm-card">
                        <div className="confirm-icon confirm-icon--error">⚠️</div>
                        <h2>Link invalid or expired</h2>
                        <p>{error}</p>
                        <Link to="/roadmap" className="btn btn-primary" style={{ marginTop: 'var(--space-5)' }}>View the roadmap</Link>
                    </div>
                )}

                {state === 'ready' && ticket && (
                    <div className="confirm-card" style={{ maxWidth: 640, textAlign: 'left' }}>
                        <h2 style={{ marginBottom: 4 }}>{ticket.request.title}</h2>
                        <p style={{ color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', marginTop: 0 }}>{ticket.request.description}</p>

                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', margin: 'var(--space-4) 0 var(--space-2)' }}>
                            Conversation with the review team
                        </div>
                        <div className="clarify-thread" style={{ maxHeight: 400 }}>
                            {ticket.thread.length === 0 && <div className="clarify-empty">No messages yet.</div>}
                            {ticket.thread.map(c => (
                                <div key={c.id} className={`clarify-msg${c.fromAdmin ? ' admin' : ' requester'}`}>
                                    <div className="clarify-meta">{c.fromAdmin ? (c.author ?? 'Review team') : 'You'} · {fmt(c.createdAt)}</div>
                                    <div className="clarify-body">{c.body}</div>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 'var(--space-4)' }}>
                            <textarea className="form-textarea" rows={3} style={{ width: '100%' }}
                                placeholder="Write your reply…" value={reply} onChange={e => setReply(e.target.value)} />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                                <button className="btn btn-primary" disabled={!reply.trim() || sending} onClick={send}>
                                    {sending ? 'Sending…' : 'Send reply'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}
